import { Component, ElementRef, ViewChild, signal, EventEmitter, Input, Output } from "@angular/core";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { EntityBase } from "../../entity/EntityBase";
import { AnalyticsService } from "../../service/analytics.service";
import { GeneralService } from "../../service/general.service";
import { ValidationService } from "../../service/validation.service";
import { State } from "../../entity/State";
import { Account } from "../../entity/Account";
import { Utility } from "../../entity/Utility";
import { User } from "../../entity/User";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ConfirmationDialog } from "../incs/confirmation-dialog.component";
import { AccountExampleDialog } from "../incs/account-example-dialog.component";
import { LoadZone } from "../../entity/LoadZone";
import { RateClass } from "../../entity/RateClass";
import { AccountConsumption } from "../../obj/AccountConsumption";
import { Company } from "../../entity/Company";
import { AccountNumberIssueDialog } from "../incs/account-number-issue-dialog.component";
import { LoaderService } from "../../service/loader.service";
import { NgxMaskDirective } from "ngx-mask";
import { firstValueFrom } from "rxjs";
import { AbstractComponent } from "../abstract/abstract.component";

export const MY_FORMATS = {
    parse: {
        dateInput: "MM/dd/yyyy",
    },
    display: {
        dateInput: "MM/dd/yyyy",
        monthYearLabel: "MMM yyyy",
        dateA11yLabel: "LL",
        monthYearA11yLabel: "MMMM yyyy",
    },
};

@Component({
    selector: "kilowant-account-add-step2",
    templateUrl: "../../../html/incs/account-add-step2.html",
    imports: [FormsModule, NgxMaskDirective, ReactiveFormsModule]
})
export class AccountAddStep2Component extends AbstractComponent {
    @Input("user") toBeUsedUser: User | null = null;
    @Input("byAdvisor") byAdvisor: boolean = false;

    fileToUpload: File | null = null;

    deleteTaxExemptCertificateDocument: boolean = false;

    @ViewChild('serviceName') serviceName: ElementRef;

    account: Account;
    @Output("accountChange") accountEmitter: EventEmitter<Account> = new EventEmitter<Account>();
    @Input("account") set setAccountValue(value: Account) {
        this.account = value;
    }

    @Output("return") returnAction = new EventEmitter<boolean>();
    @Output("taxCertificateChanged") taxCertificateChangedAction = new EventEmitter<File|null>();
    @Output("deleteTaxExemptCertificateChanged") deleteTaxExemptCertificateChangedAction: EventEmitter<boolean> = new EventEmitter<boolean>();

    consumptions: AccountConsumption[] = [];
    utilities: Utility[] = [];
    rateClasses: RateClass[] = [];
    currentAccountNumber: FormControl = new FormControl("");
    loadZones: LoadZone[] = [];
    utilityId: number | null = null;
    selectCompany: boolean = false;
    addCompanyName: boolean = false;

    dialogRef: MatDialogRef<ConfirmationDialog> = null!;
    confirmButtonActive: boolean = true;

    defaultShowAccountInfo: boolean = true;

    preRegisteredAccountUpdateMode: boolean = false;

    userCompanies: Company[];

    disableSelectorOfUtilityData: boolean = false;
    newCompanyRegistration = signal<boolean>(false);
    taxExemptAllowToAdd = signal<boolean>(true);
    taxExempt: boolean = false;
    clientType: string = "";
    warningCommercial: boolean = false;
    skipAccountNumberCheck: boolean = false;
    cityStateShow = signal<boolean>(false);

    constructor(
        router: Router,
        route: ActivatedRoute,
        protected loaderService: LoaderService,
        analyticsService: AnalyticsService,
        generalService: GeneralService,
        public validationService: ValidationService,
        dialog: MatDialog
    ) {
        super(router, route, dialog, analyticsService, generalService);
    }

    ngOnInit(): void {
        this.initPromises.push(
            this.generalService.getUtilities(this.generalService.getDefaultFuelType(), true, false, null).then((utilities) => {
                this.utilities = utilities;
                this.refreshView();
            })
        );
        super.ngOnInit();
    }

    pageStart(): void {
        if (this.toBeUsedUser != null) {
            this.account.user = this.toBeUsedUser;
        } else {
            this.skipAccountNumberCheck = true;
        }
        if (this.account.billImages == null) {
            this.account.billImages = [];
        }
        this.consumptions = [];
        this.currentAccountNumber.setValue(this.account.accountNumber);
        if (this.account.utility != null) {
            this.utilityId = this.account.utility.id;
        }

        if (this.account.utility != null && this.account.loadZone != null && this.account.rateClass != null) {
            this.disableSelectorOfUtilityData = true;
        }
        if (this.account.city != null && this.account.city.length > 0) {
            this.cityStateShow.set(true);
        }
        this.initCompanyRelatedData();
        this.reloadSelects();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    cancel(): void {
        this.returnAction.next(false);
    }

    nextStep(): void {
        this.clearErrors();
        if (this.utilityId != null && this.utilityId > 0) {
            this.account.utility = this.utilities[EntityBase.indexOf(this.utilities, new Utility(this.utilityId))];
        } else {
            this.account.utility = null!;
        }
        if (this.account.keyName == null || this.account.keyName == "") {
            this.addError("keyName");
        }
        if (this.account.utility == null) {
            this.addError("utilityId");
        }
        if (this.account.loadZone == null) {
            this.addError("loadZoneId");
        }
        if (this.account.rateClass == null) {
            this.addError("rateClassId");
        }
        if (this.account.serviceName == null || this.account.serviceName == "") {
            this.addError("serviceName");
        }
        if (this.account.addressLine1 == null || this.account.addressLine1 == "") {
            this.addError("addressLine1");
        }
        if (this.account.zip == null || this.account.zip == "") {
            this.addError("zip");
        }
        if (this.account.rateClass == null) {
            this.addError("rateClassId");
        }
        if (this.currentAccountNumber.value == null || this.currentAccountNumber.value.length == 0) {
            this.addError("accountNumber");
        }
        if (this.account.company != null && this.account.company.taxExempt == true && (this.account.company.federalTaxID == null || this.account.company.federalTaxID.length == 0)) {
            this.addError("FederalTaxID");
        }
        if (this.account.company != null && this.account.company.taxExempt == true && this.fileToUpload == null && this.account.company.taxExemptCertificateDocument == null) {
            this.addError("fileToUpload");
        }
        if (this.clientType == "Commercial" && (this.account.company == null || this.account.company.name == null || this.account.company.name.length == 0)) {
            this.addError("companyName");
        }
        if (this.errorY().showError()) {
            this.setErrorText("Please enter the data requested above in order to add your account.");
            return;
        }
        if (this.account.city == null || this.account.city.length == 0 || this.account.state == null) {
            this.addError("zip");
            this.setErrorText("Zip code is not correct.");
            return;
        }
        if (!this.validationService.validate("Account", "serviceName", this.account.serviceName, this.errorY)) {
            return;
        }
        if (!this.validationService.validate("Account", "addressLine1", this.account.addressLine1, this.errorY)) {
            return;
        }
        if (!this.validationService.validate("Account", "city", this.account.city, this.errorY)) {
            return;
        }
        if (!this.validationService.validate("Account", "zip", this.account.zip, this.errorY)) {
            return;
        }
        if (!this.validationService.validate("Account", "keyName", this.account.keyName, this.errorY)) {
            return;
        }
        if (!this.currentAccountNumber.valid) {
            this.addError("accountNumber");
            this.setErrorText("Account number is not correct.");
            return;
        }
        if (this.skipAccountNumberCheck) {
            this.returnAction.next(true);
            return;
        }
        this.generalService.ifAccountNumberUsed(this.account.utility, this.currentAccountNumber.value, this.account.id).then(res => {
            if (res.value) {
                this.addError("accountNumber");
                this.setErrorText("Account with such number already exists.");
                this.refreshView();
                return;
            } else if (res.key) {
                this.addError("accountNumber");
                this.setErrorText("accountissue");
                this.refreshView();
                return;
            } else {
                this.account.accountNumber = this.currentAccountNumber.value;
            }
            this.returnAction.next(true);
        });
    }

    reloadSelects(): void {
        if (this.utilityId != null && this.utilityId > 0) {
            let temp = this.account;
            let currentUtility = this.utilities[EntityBase.indexOf(this.utilities, new Utility(this.utilityId))];
            this.account.utility = currentUtility;
            this.currentAccountNumber.setValue(this.account.accountNumber);
            if (temp != null && currentUtility != null) {
                this.generalService.getRateClasses(currentUtility).then((rateClasses: any) => {
                    this.rateClasses = [];
                    for (let i = 0; i < rateClasses.length; i++) {
                        this.rateClasses.push(rateClasses[i]);
                    }
                    let foundRateClass = false;
                    for (let i = 0; i < this.rateClasses.length; i++) {
                        if (this.account.rateClass != null && this.account.rateClass.id == this.rateClasses[i].id) {
                            foundRateClass = true;
                            break;
                        }
                    }
                    if (!foundRateClass && this.account.rateClass != null) {
                        this.rateClasses.push(this.account.rateClass);
                    }
                    this.refreshView();
                });
                this.generalService.getLoadZones(currentUtility).then((loadZones: any) => {
                    this.loadZones = loadZones;
                    this.refreshView();
                });
            }
        }
    }

    handleFileInput(event: Event) {
        this.loaderService.setDisabled(true);
        const target = event.target as HTMLInputElement;
        this.fileToUpload = (target.files as FileList).item(0) as File;
        this.taxCertificateChangedAction.next(this.fileToUpload);
        this.resetError("fileToUpload");
        this.refreshView()
    }

    deleteFile() {
        this.fileToUpload = null;
        this.taxCertificateChangedAction.next(this.fileToUpload);
        this.refreshView();
    }

    downloadFile(ifExisting: boolean, id: number | null) {
        if (!ifExisting) {
            this.generalService.sendFileToClient(this.fileToUpload);
        } else if (id != null) {
            this.generalService.openDocument(id);
        }
    }

    deleteExistingFile(key: number) {
        this.deleteTaxExemptCertificateDocument = true;
        this.deleteTaxExemptCertificateChangedAction.next(this.deleteTaxExemptCertificateDocument);
        if (this.toBeUsedUser != null && this.toBeUsedUser.company != null) {
            this.toBeUsedUser.company.taxExemptCertificateDocument = null!;
        }
        this.refreshView()
    }

    openExampleDialog(firstTime: boolean): void {
        if (firstTime && !this.defaultShowAccountInfo) {
            return;
        }
        this.defaultShowAccountInfo = false;
        let dialogRef = this.dialog.open(AccountExampleDialog, {
            disableClose: false
            , panelClass: "example-popup"
        });
        var t = this;
        dialogRef.afterClosed().subscribe(result => {
            t.serviceName.nativeElement.focus();
        });
    }

    getAccountIdent(a: Account): string {
        return Account.accountIdent(a);
    }

    reportAccountIssue(): void {
        var dialogRef = this.dialog.open(AccountNumberIssueDialog, {
            disableClose: false,
            panelClass: "i-icon-popup"
        });
        dialogRef.componentInstance.info = "Please, provide more details about your account (optional).";
        dialogRef.componentInstance.message = "My Account Number: " + this.currentAccountNumber.value + " has been used without my authorization. Please, investigate this.";
        dialogRef.componentInstance.utility = this.account.utility;
        dialogRef.componentInstance.accountNumber = this.currentAccountNumber.value;

        dialogRef.afterClosed().subscribe((result: any) => {
            dialogRef = null!;
        });
    }

    taxExemptChanged(): void {
        if (this.newCompanyRegistration() || this.taxExemptAllowToAdd()) {
            this.account!.company!.taxExempt = this.taxExempt;
            return;
        }
        if (this.account.company != null) {
            this.account.company.taxExempt = this.taxExempt;
        }
    }

    setNewCompany(): void {
        this.taxExempt = false;
        this.account.company = new Company();
        this.taxExempt = false;
        this.clearErrors();
        this.newCompanyRegistration.set(true);
    }

    selectExistingCompany(): void {
        this.taxExempt = this.account.company?.taxExempt!;
        this.taxExemptAllowToAdd.set(true);
        this.clearErrors();
        this.refreshView();
    }

    switchClientType(): void {
        if (this.clientType == "Commercial" && this.toBeUsedUser != null && this.toBeUsedUser.company == null) {
            if (this.account.company == null) {
                this.account.company = new Company();
            }
        }
        if (this.clientType == "Residential") {
            this.account.company = null;
        }
        this.clearErrors();
        this.refreshView();
    }

    switchToCompanySelect(): void {
        this.account.company = null;
        this.newCompanyRegistration.set(false);
        this.clearErrors();
    }

    getStateName(state: State | null): string {
        if (state == null) {
            return "";
        }
        return state.stateCode;
    }

    zipChanged(): void {
        this.resetError('zip');
        this.account.city = "";
        this.account.state = null;
        if (this.account.zip != null && this.account.zip?.length > 2) {
            let request = this.generalService.getStateCityByZip(this.account.zip);
            if (request == null) {
                return;
            }
            firstValueFrom(request).then(data => {
                if (data != null) {
                    this.account.state = data.key;
                    this.account.city = data.value;
                    this.cityStateShow.set(true);
                    this.refreshView();
                }
            });
        }
    }

    initCompanyRelatedData(): void {
        if (this.byAdvisor) {
            this.clientType = (this.account.company == null) ? 'Residential' : 'Commercial';
            this.newCompanyRegistration.set(false);
            if (this.clientType == "Residential" && this.account.rateClass?.isCommercial!) {
                this.warningCommercial = true;
            }
            this.taxExempt = (this.account.company?.taxExempt == true);
            this.refreshView();
            return;
        }
        if (this.toBeUsedUser != null) {
            firstValueFrom(this.generalService.getUsersCompanies()).then((companies: any) => {
                this.userCompanies = [];
                for (let i = 0; i < companies.length; i++) {
                    if (companies[i] != null) {
                        this.userCompanies.push(companies[i]);
                    }
                }
                if (this.userCompanies.length == 0) {
                    this.newCompanyRegistration.set(true);
                    // Only a COMMERCIAL account gets a blank Company to fill in.
                    //
                    // This callback resolves AFTER the synchronous tail of this
                    // method, which sets clientType and — for a residential rate
                    // class — clears account.company. Creating one here
                    // unconditionally put an empty Company back, so Account/Add
                    // shipped `company = { id: 0, name: "" }` and the API rejected
                    // it with 400 "Company name is required." (ApiUsersController.cs:426,
                    // and correctly: Company.Name is NOT NULL). The buyer saw an
                    // error about a field that is hidden for Residential and had no
                    // control to satisfy it. clientType is already final by the time
                    // we get here, so it is the right thing to test.
                    if (this.account.company == null && this.clientType != "Residential") {
                        this.account.company = new Company();
                    }
                }
                this.refreshView();
            });
        } else {
            this.newCompanyRegistration.set(true);
            if (this.account.company == null) {
                this.account.company = new Company();
            }
        }
        if (this.account.rateClass?.isCommercial!) {
            this.clientType = "Commercial";
        } else {
            this.clientType = "Residential"
            this.account.company = null;
        }
        this.taxExempt = false;
        this.selectExistingCompany();
        this.refreshView();
    }
}
